import { e2ee } from './crypto/lazy.js'
import { inhaltLesen } from './media/anhang.js'
import { t } from './i18n/t.js'

/**
 * Die Vorschauzeile einer Unterhaltung — „Sonntags gibt es Streuselkuchen".
 *
 * ## Warum es das erst jetzt gibt
 *
 * In der Chatliste stand bis hierher bewusst kein Textausschnitt. Die
 * Begründung stand in `Chat.tsx` und war richtig: Ein Ausschnitt läge nur vor,
 * wenn der Client jede Unterhaltung vorab entschlüsselte — Rechenzeit für eine
 * Zeile Text, auf genau den Geräten, für die diese App gebaut ist.
 *
 * Der Grund, warum es jetzt trotzdem geht: Der Server liefert seit dieser
 * Phase je Raum **nur die letzte** Nachricht mit (`RoomView.lastMessage`).
 * Zwölf Entschlüsselungen statt zwölftausend. Das ist der Unterschied zwischen
 * „vorab den ganzen Verlauf" und „die eine Zeile, die man sieht".
 *
 * ## Warum ein Modul und kein Hook je Zeile
 *
 * Die Liste steht auf zwei Bildschirmen — Startseite und Chatliste. Zwei
 * eigene Ladewege wären zwei Stellen, an denen dieselbe Nachricht ein zweites
 * Mal entschlüsselt würde. Bei MLS ist das kein doppelter Aufwand, sondern ein
 * Fehler: Der Schlüssel dieser Generation ist danach verbraucht. Deshalb genau
 * ein Weg, und ein gemeinsamer Zwischenspeicher.
 */

export interface Vorschau {
  /** Was in der Zeile steht — schon gekürzt und mit Symbol für Anhänge. */
  text: string
  /** Wer sie geschrieben hat. Im Familienchat steht der Name davor. */
  senderId: string
  /** Ließ sie sich nicht öffnen? Dann steht der Grund da, grau statt normal. */
  unlesbar: boolean
}

export interface LetzteNachricht {
  id: string
  senderId: string
  envelope: string
  hasAttachments: boolean
}

/** Länger braucht keine Zeile — der Rest passt ohnehin nicht auf den Schirm. */
const HOECHSTENS = 90

const zwischen = new Map<string, { messageId: string; vorschau: Vorschau }>()
/** Läuft gerade eine Entschlüsselung für diesen Raum? Verhindert Doppelarbeit. */
const unterwegs = new Map<string, Promise<void>>()

/** Was schon bekannt ist — ohne Krypto, für die erste Anzeige. */
export function bekannteVorschau(roomId: string, messageId: string | null): Vorschau | undefined {
  if (!messageId) return undefined
  const eintrag = zwischen.get(roomId)
  return eintrag?.messageId === messageId ? eintrag.vorschau : undefined
}

/**
 * Entschlüsselt, was noch fehlt.
 *
 * Gibt zurück, ob etwas Neues dazugekommen ist — daran entscheidet der
 * Aufrufer, ob er sich neu zeichnen muss.
 */
export async function vorschauenLaden(
  raeume: Array<{ id: string; lastMessage: LetzteNachricht | null }>,
): Promise<boolean> {
  const offen = raeume.filter((r) =>
    r.lastMessage
    && zwischen.get(r.id)?.messageId !== r.lastMessage.id
    && !unterwegs.has(r.id))

  if (offen.length === 0) {
    await Promise.all([...unterwegs.values()])
    return false
  }

  const laeufe = offen.map((r) => {
    const lauf = (async () => {
      try {
        zwischen.set(r.id, {
          messageId: r.lastMessage!.id,
          vorschau: await eineLaden(r.id, r.lastMessage!),
        })
      } catch {
        // Kein Schlüssel, keine Verbindung — dann bleibt die Zeile ohne
        // Vorschau. Eine Liste, die deswegen leer bliebe, wäre der schlechtere
        // Ausfall.
      } finally {
        unterwegs.delete(r.id)
      }
    })()
    unterwegs.set(r.id, lauf)
    return lauf
  })

  await Promise.all(laeufe)
  return true
}

async function eineLaden(roomId: string, letzte: LetzteNachricht): Promise<Vorschau> {
  const krypto = await e2ee()

  /*
   * Drei Quellen, in dieser Reihenfolge — und die Reihenfolge ist der Punkt.
   *
   *  1. `vorschauText`: Diese Nachricht wurde schon einmal für die Vorschau
   *     geöffnet, etwa vor dem letzten Neuladen der Seite. Ein zweiter Versuch
   *     würde scheitern, nicht bloß Zeit kosten.
   *  2. `eigenerText`: Selbst gesendet — für den Absender ist die eigene
   *     Nachricht nie zu entschlüsseln, das ist bei MLS so vorgesehen.
   *  3. Erst dann wirklich entschlüsseln, und das Ergebnis gleich merken.
   */
  const gemerkt = await krypto.vorschauText(roomId, letzte.id)
  if (gemerkt !== null) return fertig(gemerkt, letzte)

  const eigener = await krypto.eigenerText(letzte.id)
  if (eigener !== null) return fertig(eigener, letzte)

  const res = await krypto.nachrichtEntschluesseln(roomId, letzte.envelope)
  if (res.art !== 'text') return { text: res.grund, senderId: letzte.senderId, unlesbar: true }

  await krypto.vorschauMerken(roomId, letzte.id, res.text)
  return fertig(res.text, letzte)
}

function fertig(roh: string, letzte: LetzteNachricht): Vorschau {
  // Der rohe Klartext kann Text sein oder Text MIT Anhängen — dieselbe Marke
  // wie im Chat, gelesen von derselben Funktion.
  const inhalt = inhaltLesen(roh)
  const text = inhalt.text.trim()

  /*
   * Ein Bild ohne Bildunterschrift braucht trotzdem eine Zeile — sonst sieht
   * die Unterhaltung aus, als hätte niemand etwas geschickt.
   *
   * Es steht bewusst „Anhang" da und nicht „Foto": WAS drangehangen wurde,
   * steht in den verschlüsselten Angaben zur Datei, und die dafür zu öffnen
   * wäre für eine Zeile in einer Liste zu viel. Lieber allgemein und richtig
   * als genau und geraten.
   */
  const mitAnhang = letzte.hasAttachments || inhalt.anhaenge.length > 0
  const beschriftung = mitAnhang ? `📎 ${t('Anhang')}` : ''

  const zusammen = text
    ? (mitAnhang ? `${beschriftung} ${text}` : text)
    : beschriftung

  return { text: kuerzen(zusammen), senderId: letzte.senderId, unlesbar: false }
}

/**
 * Kürzt auf eine Zeile — und zwar an einer Wortgrenze.
 *
 * Mitten im Wort abzuschneiden liest sich wie ein Fehler; „Sonntags gibt es
 * Streusel…" ist eine Kürzung, „Sonntags gibt es Streuselk…" wirkt kaputt.
 * Zeilenumbrüche werden zu Leerzeichen: In einer einzeiligen Vorschau wäre ein
 * Umbruch eine Lücke ohne Grund.
 */
export function kuerzen(text: string): string {
  const eine = text.replace(/\s+/gu, ' ').trim()
  if (eine.length <= HOECHSTENS) return eine
  const schnitt = eine.slice(0, HOECHSTENS)
  const luecke = schnitt.lastIndexOf(' ')
  return `${luecke > HOECHSTENS / 2 ? schnitt.slice(0, luecke) : schnitt}…`
}

/** Nach dem Abmelden: Was hier liegt, gehört zum vorigen Konto. */
export function vorschauenVergessen(): void {
  zwischen.clear()
  unterwegs.clear()
}
